import type { Signer, SignMarker } from '@/lib/signTypes';
/**
 * pre204: Kamera-Command-Responder für den mobilen Gast (iPhone/Android).
 *
 * Der Berater sendet über den DataChannel `bamoa-cam-ctrl` dieselben Befehle wie an die
 * Stele (camera.scan / camera.roi.zoom|move|reset / light.set). Die Stele setzt sie über
 * ihr Master-Track-Modell um — ein Telefon hat das nicht, daher hier eine mobil-taugliche,
 * defensive Umsetzung:
 *
 *  - Scan:  aktuelles Vollbild der Kamera als JPEG greifen und gechunkt zurücksenden
 *           (scan.start → binäre Chunks → scan.end), identisch zum Stele-Protokoll.
 *  - Zoom:  Canvas-Crop-Pipeline (funktioniert auch auf iOS, wo der `zoom`-Constraint fehlt).
 *           Der ausgehende Videotrack wird durch einen zugeschnittenen Canvas-Stream ersetzt;
 *           das Selbstbild des Gastes bleibt die volle Kamera.
 *  - Licht: MediaStreamTrack-`torch`-Constraint (nur Android; iOS Safari unterstützt es nicht).
 *
 * Kamerawechsel (Front/Rück) läuft über `rtc:swap` und wird im useGuestRtc gehandhabt
 * (getUserMedia(facingMode) + replaceTrack), siehe replaceSource().
 */

export const CAM_DATACHANNEL_LABEL = 'bamoa-cam-ctrl';
const SCAN_CHUNK_SIZE = 16 * 1024;

function clamp01(v: number) { return Math.max(0, Math.min(1, v)); }

type OutputCb = (track: MediaStreamTrack) => void;

export class GuestCamController {
  private source: MediaStreamTrack;
  private zoom = 1;
  private cx = 0.5;
  private cy = 0.5;
  private video: HTMLVideoElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private raf = 0;
  private outTrack: MediaStreamTrack | null = null;
  private onOutput: OutputCb;
  private srcW = 1280;
  private srcH = 720;

  constructor(source: MediaStreamTrack, onOutput: OutputCb) {
    this.source = source;
    this.onOutput = onOutput;
    const st = source.getSettings?.() || {};
    this.srcW = st.width || 1280;
    this.srcH = st.height || 720;
  }

  /** Der Track, der aktuell an den Berater gehen soll (Crop-Canvas bei Zoom, sonst Quelle). */
  get outgoingTrack(): MediaStreamTrack { return this.outTrack || this.source; }

  private ensurePipeline() {
    if (this.canvas) return;
    try {
      const video = document.createElement('video');
      video.muted = true;
      (video as any).playsInline = true;
      video.autoplay = true;
      video.srcObject = new MediaStream([this.source]);
      void video.play().catch(() => { /* */ });
      const canvas = document.createElement('canvas');
      canvas.width = this.srcW; canvas.height = this.srcH;
      const ctx = canvas.getContext('2d');
      this.video = video; this.canvas = canvas; this.ctx = ctx;

      const draw = () => {
        const v = this.video; const c = this.canvas; const g = this.ctx;
        if (v && c && g) {
          const vw = v.videoWidth || this.srcW;
          const vh = v.videoHeight || this.srcH;
          if (vw && vh) {
            if (c.width !== vw || c.height !== vh) { c.width = vw; c.height = vh; }
            const sw = vw / this.zoom;
            const sh = vh / this.zoom;
            let sx = this.cx * vw - sw / 2;
            let sy = this.cy * vh - sh / 2;
            sx = Math.max(0, Math.min(vw - sw, sx));
            sy = Math.max(0, Math.min(vh - sh, sy));
            g.drawImage(v, sx, sy, sw, sh, 0, 0, c.width, c.height);
          }
        }
        this.raf = requestAnimationFrame(draw);
      };
      this.raf = requestAnimationFrame(draw);

      const out = (canvas as any).captureStream?.(30) as MediaStream | undefined;
      const t = out?.getVideoTracks()[0] || null;
      if (t) { this.outTrack = t; this.onOutput(t); }
    } catch (e) {
      console.warn('[guest-cam] Zoom-Pipeline fehlgeschlagen', e);
    }
  }

  setZoom(factor: number) {
    this.zoom = Math.max(1, Math.min(4, Number(factor) || 1));
    if (this.zoom > 1.001) this.ensurePipeline();
  }

  move(dx: number, dy: number) {
    if (this.zoom <= 1.001) return;
    // dx/dy sind normalisiert (0..1); auf den sichtbaren Ausschnitt skalieren.
    this.cx = clamp01(this.cx + (Number(dx) || 0));
    this.cy = clamp01(this.cy + (Number(dy) || 0));
  }

  reset() {
    this.zoom = 1; this.cx = 0.5; this.cy = 0.5;
    // Pipeline (falls aktiv) gibt danach das Vollbild aus — kein Track-Wechsel nötig.
  }

  async setTorch(on: boolean): Promise<boolean> {
    try {
      const caps = (this.source.getCapabilities?.() as any) || {};
      if (!('torch' in caps) || !caps.torch) return false;
      await this.source.applyConstraints({ advanced: [{ torch: !!on }] } as any);
      return true;
    } catch {
      return false;
    }
  }

  /** Kamera getauscht (Front/Rück): Quelle austauschen. */
  replaceSource(track: MediaStreamTrack) {
    const old = this.source;
    this.source = track;
    const st = track.getSettings?.() || {};
    this.srcW = st.width || this.srcW;
    this.srcH = st.height || this.srcH;
    if (this.video) {
      this.video.srcObject = new MediaStream([track]);
      void this.video.play().catch(() => { /* */ });
    } else {
      // Keine Pipeline aktiv → der direkte Track geht raus.
      this.onOutput(track);
    }
    if (old && old !== track) { try { old.stop(); } catch { /* */ } }
  }

  /** Vollbild der aktuellen Kamera greifen (beste Qualität, unabhängig vom Zoom). */
  async grabFrame(format: string): Promise<Blob> {
    let video = this.video;
    let temp = false;
    if (!video) {
      temp = true;
      video = document.createElement('video');
      video.muted = true;
      (video as any).playsInline = true;
      video.autoplay = true;
      video.srcObject = new MediaStream([this.source]);
      await video.play().catch(() => { /* */ });
      await new Promise((r) => setTimeout(r, 150));
    }
    const vw = video.videoWidth || this.srcW;
    const vh = video.videoHeight || this.srcH;
    const c = document.createElement('canvas');
    c.width = vw; c.height = vh;
    const g = c.getContext('2d');
    if (!g) throw new Error('kein 2d-Context');
    g.drawImage(video, 0, 0, vw, vh);
    if (temp) { try { video.srcObject = null; } catch { /* */ } }
    const mime = format === 'png' ? 'image/png' : format === 'webp' ? 'image/webp' : 'image/jpeg';
    return await new Promise<Blob>((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('toBlob null'))), mime, 0.92));
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.video) { try { this.video.srcObject = null; } catch { /* */ } }
    this.video = null; this.canvas = null; this.ctx = null; this.outTrack = null;
  }
}

export interface GuestCamHooks {
  onLight?: (on: boolean) => Promise<boolean>;
  /** pre219: Der Berater schickt ein PDF zur Unterschrift (echtes sign.submit-Protokoll). */
  onSignRequest?: (pdf: ArrayBuffer, filename: string, sigId: string, signers?: Signer[], markers?: SignMarker[], requireIdent?: boolean, requireOtp?: boolean) => void;
}

/** Verdrahtet den bamoa-cam-ctrl-DataChannel mit dem Controller. */
export function attachGuestCamResponder(dc: RTCDataChannel, ctrl: GuestCamController, hooks?: GuestCamHooks) {
  dc.binaryType = 'arraybuffer';
  // pre219: Puffer für ein vom Berater gesendetes Signatur-PDF (start → binäre Chunks → end).
  let signBuf: { sigId: string; filename: string; signers?: Signer[]; markers?: SignMarker[]; requireIdent?: boolean; requireOtp?: boolean; chunks: ArrayBuffer[] } | null = null;
  dc.onmessage = async (e: MessageEvent) => {
    if (typeof e.data !== 'string') {
      if (signBuf) signBuf.chunks.push(e.data as ArrayBuffer); // Signatur-Dokument-Chunk
      return;
    }
    let cmd: any;
    try { cmd = JSON.parse(e.data); } catch { return; }
    switch (cmd?.type) {
      case 'sign.submit.start':
        signBuf = { sigId: cmd.sigId, filename: cmd.filename || 'Dokument.pdf', signers: cmd.signers, markers: cmd.markers, requireIdent: !!cmd.requireIdent, requireOtp: !!cmd.requireOtp, chunks: [] };
        break;
      case 'sign.submit.end': {
        if (signBuf && signBuf.sigId === cmd.sigId) {
          const total = signBuf.chunks.reduce((n, c) => n + c.byteLength, 0);
          const merged = new Uint8Array(total);
          let off = 0;
          for (const c of signBuf.chunks) { merged.set(new Uint8Array(c), off); off += c.byteLength; }
          const { sigId, filename, signers, markers, requireIdent, requireOtp } = signBuf; signBuf = null;
          // pre220: der Berater wartet auf sign.submit.ack (stoppt das 5×-Neusenden), dann Status sign.received.
          try { dc.send(JSON.stringify({ type: 'sign.submit.ack', sigId })); } catch { /* */ }
          try { dc.send(JSON.stringify({ type: 'sign.received', sigId })); } catch { /* */ }
          hooks?.onSignRequest?.(merged.buffer, filename, sigId, signers, markers, requireIdent, requireOtp);
        }
        break;
      }
      case 'sign.abort':
        signBuf = null;
        break;
      case 'camera.scan':
        await sendScan(dc, ctrl, cmd.format || 'jpeg', cmd.role || 'main');
        break;
      case 'light.set': {
        const ok = hooks?.onLight ? await hooks.onLight(!!cmd.on) : await ctrl.setTorch(!!cmd.on);
        // pre211: 'on' = angeforderter Zustand, 'ok' = Erfolg. Der Berater setzt bei ok=true
        // lightOn=on, bei ok=false wird revertiert. Ohne 'ok' galt bisher JEDES Ergebnis als
        // Fehler → Button-Zustand falsch → zweiter Druck (aus) sendete wieder 'an'.
        try { dc.send(JSON.stringify({ type: 'light.state', on: !!cmd.on, ok, ...(ok ? {} : { error: 'torch-unsupported' }) })); } catch { /* */ }
        break;
      }
      case 'camera.roi.zoom': ctrl.setZoom(cmd.factor ?? 1); break;
      case 'camera.roi.move': ctrl.move(cmd.dx || 0, cmd.dy || 0); break;
      case 'camera.roi.reset': ctrl.reset(); break;
      default: break; // roi.set etc. auf dem Telefon nicht unterstützt
    }
  };
  // Fähigkeiten melden (informativ).
  try { dc.send(JSON.stringify({ type: 'camera.capabilities', roles: ['main'], canScan: true, canZoom: true })); } catch { /* */ }
}

// pre219: Ein signiertes PDF über das echte sign.result-Protokoll an den Berater zurücksenden.
export async function sendSignResult(dc: RTCDataChannel, sigId: string, filename: string, bytes: ArrayBuffer, identProof?: unknown, identStatus?: string, otpCode?: string) {
  try { dc.send(JSON.stringify({ type: 'sign.signing', sigId })); } catch { /* */ }
  const total = bytes.byteLength;
  const totalChunks = Math.max(1, Math.ceil(total / SCAN_CHUNK_SIZE));
  dc.send(JSON.stringify({ type: 'sign.result.start', sigId, filename, totalBytes: total, totalChunks, chunkSize: SCAN_CHUNK_SIZE, identProof, identStatus, otpCode }));
  for (let i = 0; i < totalChunks; i++) {
    while (dc.bufferedAmount > 4 * 1024 * 1024) await new Promise((r) => setTimeout(r, 20));
    const start = i * SCAN_CHUNK_SIZE;
    dc.send(bytes.slice(start, Math.min(total, start + SCAN_CHUNK_SIZE)));
  }
  dc.send(JSON.stringify({ type: 'sign.result.end', sigId }));
}

// pre215: Beliebige Bilddatei (z. B. nativer Screenshot aus der Fotos-App) über dasselbe
// scan-Protokoll an den Berater senden — landet dort im SnapshotViewer. Rein P2P.
export async function sendImageBytes(dc: RTCDataChannel, buf: ArrayBuffer, format: string, role = 'main', mime?: string, name?: string) {
  const scanId = Math.random().toString(36).slice(2);
  const total = buf.byteLength;
  const totalChunks = Math.max(1, Math.ceil(total / SCAN_CHUNK_SIZE));
  dc.send(JSON.stringify({ type: 'camera.scan.start', scanId, role, format, mime, name, totalBytes: total, totalChunks, chunkSize: SCAN_CHUNK_SIZE }));
  for (let i = 0; i < totalChunks; i++) {
    while (dc.bufferedAmount > 4 * 1024 * 1024) await new Promise((r) => setTimeout(r, 20));
    const start = i * SCAN_CHUNK_SIZE;
    dc.send(buf.slice(start, Math.min(total, start + SCAN_CHUNK_SIZE)));
  }
  dc.send(JSON.stringify({ type: 'camera.scan.end', scanId }));
}

async function sendScan(dc: RTCDataChannel, ctrl: GuestCamController, format: string, role: string) {
  const scanId = Math.random().toString(36).slice(2);
  try {
    const blob = await ctrl.grabFrame(format);
    const buf = await blob.arrayBuffer();
    const total = buf.byteLength;
    const totalChunks = Math.max(1, Math.ceil(total / SCAN_CHUNK_SIZE));
    dc.send(JSON.stringify({ type: 'camera.scan.start', scanId, role, format, totalBytes: total, totalChunks, chunkSize: SCAN_CHUNK_SIZE }));
    for (let i = 0; i < totalChunks; i++) {
      while (dc.bufferedAmount > 4 * 1024 * 1024) await new Promise((r) => setTimeout(r, 20));
      const start = i * SCAN_CHUNK_SIZE;
      dc.send(buf.slice(start, Math.min(total, start + SCAN_CHUNK_SIZE)));
    }
    dc.send(JSON.stringify({ type: 'camera.scan.end', scanId }));
  } catch (err: any) {
    try { dc.send(JSON.stringify({ type: 'camera.scan.error', role, error: String(err?.message || err) })); } catch { /* */ }
  }
}
